import React, { useEffect, useState, useMemo } from "react";
import { formatNumber } from "../../../utils/format";
import { MobileDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  Box,
  Grid,
  IconButton,
  Button,
  Tooltip,
  TextField,
  Menu,
  MenuItem,
  AlertTitle,
  Alert,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Typography,
  SvgIcon,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import moment from "moment";
import AddIcon from "@mui/icons-material/Add";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";

import { useLocation, useNavigate } from "react-router-dom";

import Close from "@mui/icons-material/Close";
import { MaterialReactTable } from "material-react-table";
import { apiData } from "../../../services/api_service";
import { fetchReportsAPI } from "../../../config";
import Layout from "../../../layout/Layout";
import { dateColumnFilterProps } from "../../../components/MrtDateColumnFilter";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  FileDownload,
  PanoramaFishEye,
  RemoveRedEyeRounded,
  ViewListRounded,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import { fitColumns } from "../../../utils/excelFit";
import ProductWiseInventory from "./ProductWiseInventory";
import {
  reportViewToggleButtonSx,
  reportViewToggleGroupSx,
} from "./reportViewToggleStyles";
import { buildExportFileName } from "../../../utils/exportFileName";
import ListFilterBar, { emptyListFilters } from "../../../components/ListFilterBar";
import { writeXlsxFrozen } from "../../../utils/xlsxFreeze";

const StoreInventoryReports = () => {
  const [tblList, setTblList] = useState([]);
  // Store filter (narrows both the Store-wise and Product-wise views).
  const [bar, setBar] = useState({ ...emptyListFilters });

  const params = useParams();
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  // Set only by Search/Clear — what's actually applied to the table right
  // now. Export and the auto-refetch effect below read these, not
  // fromDate/toDate directly, so neither drops nor exports a stale filter.
  const [selectedFromDate, setSelectedFromDate] = useState(null);
  const [selectedToDate, setSelectedToDate] = useState(null);
  const { storeId } = params;

  const [summary, setSummary] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 100,
  });
  const location = useLocation();

  const [message, setMessage] = useState(null);
  const [selectedRowIds, setSelectedRowIds] = useState([]);

  const [rowCount, setRowCount] = useState(0);
  const [storeName, setStoreName] = useState("");
  // Top-level view: "store" (stores list) or "product" (products across stores).
  // Opened from a dashboard Inventory Alerts line: show the product-wise view
  // pre-filtered to that alert's products (cleared with "Clear filter").
  const [dashAlert, setDashAlert] = useState(() =>
    location.state?.alertFilter ? location.state : null
  );
  const [viewMode, setViewMode] = useState(() => {
    if (location.state?.alertFilter || location.state?.view === "product") return "product";
    return sessionStorage.getItem("inventoryReportViewMode") || "store";
  });

  useEffect(() => {
    sessionStorage.setItem("inventoryReportViewMode", viewMode);
  }, [viewMode]);

  const [loading, setLoading] = useState(true);
  const [exportData, setExportData] = useState([]);

  const [successMessage, setSuccessMessage] = useState("");

  const initialFilters = {
    department: "",

    district: "",
    // date: null,
    from_date: "",
    to_date: "",
    // total_events: '',
  };

  const handleClear = () => {
    setFromDate(null);
    setToDate(null);
    setSelectedFromDate(null);
    setSelectedToDate(null);
    fetchData(null, null);
  };

  const handleSearch = () => {
    setSelectedFromDate(fromDate);
    setSelectedToDate(toDate);
    fetchData(fromDate, toDate);
  };

  const [filters, setFilters] = useState(initialFilters);

  const navigate = useNavigate();

  useEffect(() => {
    setTimeout(() => {
      setMessage(null);
    }, 3000);
  }, [message]);

  const handleExportData = async () => {
    try {
      setLoading(true);

      const payload = {
        pagination: JSON.stringify({
          pageIndex: 0,
          pageSize: 10000000,
        }),
        fromDate: selectedFromDate ? dayjs(selectedFromDate).format("YYYY-MM-DD") : null,
        toDate: selectedToDate ? dayjs(selectedToDate).format("YYYY-MM-DD") : null,
        sorting: sorting.length > 0 ? JSON.stringify(sorting) : null,
        search: columnFilters.length > 0 ? JSON.stringify(columnFilters) : null,
        globalSearch: globalFilter || null,
        store_id: storeId || null,
      };

      const api = storeId ? `${fetchReportsAPI}/${storeId}` : fetchReportsAPI;
      const response = await apiData("get", api, payload);

      if (!response?.data?.result || response.data?.result.length === 0) {
        alert("No data to export!");
        return;
      }

      let exportData = [];
      if (!storeId) {
        // Same stores as on screen: honour the Store dropdown.
        const storeRowsForExport = Number(bar.storeId) > 0
          ? response.data.result.filter((r) => Number(r.store_id) === Number(bar.storeId))
          : response.data.result;
        exportData = storeRowsForExport.map((item, index) => ({
          "#": index + 1,
          "Store Name": item.store_name || "",
          "Total No. of Products": item.total_products || 0,
          "Purchase Orders-Created": item.total_purchase_orders || 0,
          "Purchase Orders-Cancelled": item.cancelled_purchase_orders || 0,
          "Invoices-Created": item.total_sales_orders || 0,
          "Invoices-Cancelled": item.cancelled_sales_orders || 0,
        }));
        // TOTAL row: sum of the values written in the rows above (= on-screen footer)
        const sumCol = (k) => exportData.reduce((s, r) => s + (Number(r[k]) || 0), 0);
        exportData.push({
          "#": "",
          "Store Name": "TOTAL",
          "Total No. of Products": sumCol("Total No. of Products"),
          "Purchase Orders-Created": sumCol("Purchase Orders-Created"),
          "Purchase Orders-Cancelled": sumCol("Purchase Orders-Cancelled"),
          "Invoices-Created": sumCol("Invoices-Created"),
          "Invoices-Cancelled": sumCol("Invoices-Cancelled"),
        });
      }

      // Single-store view shows the product-level table (Innercolumns), so export
      // those columns, with a TOTAL row summing the rows written.
      if (storeId) {
        exportData = response.data.result.map((item, index) => ({
          "#": index + 1,
          "Product Name": item.product_name || "",
          "In Stock": Number(item.in_stock || 0),
          "Added Quantity": Number(item.added_quantity || 0),
          "Transferred Quantity": Number(item.transferred_in || 0),
          "Deducted Quantity": Number(item.deducted_quantity || 0),
          "Expires In (Days)": item.expires_in ?? "",
          "Manufactured Date": item.manufacture_date
            ? moment(item.manufacture_date).format("DD-MM-YYYY")
            : "",
        }));
        const sumInner = (k) => exportData.reduce((s, r) => s + (Number(r[k]) || 0), 0);
        exportData.push({
          "#": "",
          "Product Name": "TOTAL",
          "In Stock": sumInner("In Stock"),
          "Added Quantity": sumInner("Added Quantity"),
          "Transferred Quantity": sumInner("Transferred Quantity"),
          "Deducted Quantity": sumInner("Deducted Quantity"),
        });
      }

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      fitColumns(ws);
      XLSX.utils.book_append_sheet(wb, ws, "Store_inventory_Report");

      const fileName = buildExportFileName("Store_inventory_Report", [
        {
          label: "Scope",
          value: storeId
            ? storeName || storeId
            : Number(bar.storeId) > 0
              ? (tblList || []).find((r) => Number(r.store_id) === Number(bar.storeId))?.store_name || "Store"
              : "All_Stores",
        },
        selectedFromDate
          ? { label: "From", value: dayjs(selectedFromDate).format("DD-MM-YYYY") }
          : null,
        selectedToDate
          ? { label: "To", value: dayjs(selectedToDate).format("DD-MM-YYYY") }
          : null,
      ]);
      writeXlsxFrozen(XLSX, wb, fileName);
      setSuccessMessage("Excel Downloaded Successfully!");
    } catch (error) {
      console.error("Export failed:", error);
      alert("Failed to export data.");
    } finally {
      setLoading(false);
    }
  };

  async function fetchData(fromDate, toDate) {
    try {
      setLoading(true);
      const payload = {
        pagination: JSON.stringify({
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
        }),
        sorting: sorting.length > 0 ? JSON.stringify(sorting) : null,
        fromDate: fromDate ? dayjs(fromDate).format("YYYY-MM-DD") : null,
        toDate: toDate ? dayjs(toDate).format("YYYY-MM-DD") : null,
        search: columnFilters.length > 0 ? JSON.stringify(columnFilters) : null,
        globalSearch: globalFilter || null,
        store_id: storeId || null,
      };
      const api = storeId ? `${fetchReportsAPI}/${storeId}` : fetchReportsAPI;
      const response = await apiData("get", api, payload);
      if (response.success && response.data.result) {
        setStoreName(response.data?.result[0]?.store_name || "");
        setRowCount(response.data?.total);
        setTblList(response.data?.result);
        setSummary(response.data?.summary);
        // setExportData(exportData.data?.result);
      }
    } catch (error) {
      console.error("Failed to fetch data", error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    // Re-applies whatever date range was last actually searched (if any) —
    // previously called with no args here, which silently dropped an
    // applied date filter the moment a column filter, sort, or page changed.
    fetchData(selectedFromDate, selectedToDate);
  }, [globalFilter, columnFilters, sorting, pagination, storeId]);

  // Store-wise footer: all stores (API total over every page), or just the one
  // store picked in the Store dropdown, so the footer matches the rows shown.
  const viewSummary = useMemo(() => {
    if (storeId || !(Number(bar.storeId) > 0)) return summary;
    const keys = ["total_products", "total_purchase_orders", "cancelled_purchase_orders", "total_sales_orders", "cancelled_sales_orders"];
    const rows = (tblList || []).filter((r) => Number(r.store_id) === Number(bar.storeId));
    const out = {};
    keys.forEach((k) => (out[k] = rows.reduce((t, r) => t + (Number(r[k]) || 0), 0)));
    return out;
  }, [summary, tblList, bar.storeId, storeId]);

  const columns = useMemo(() => {
    return [
      {
        accessorKey: "store_name",
        header: "Store Name",
        size: 10,
        Footer: () => <Typography fontWeight={600}>TOTAL</Typography>,
      },

      {
        accessorKey: "total_products",
        header: "Total No. of Products",
        size: 10,
        Cell: ({ row }) =>
          // <Tooltip title="View Product Details" arrow>
          //   <Typography
          // color="primary"
          // sx={{ cursor: "pointer" }}
          // onClick={() => {
          //   setStoreName(row.original.store_name);
          //   navigate(`/reports/stock-report/${row.original.store_id}`);
          // }}
          // >
          formatNumber(row.original.total_products),
        //   </Typography>
        // </Tooltip>
        Footer: () =>
          viewSummary?.total_products ? (
            <Typography fontWeight={600}>{formatNumber(viewSummary.total_products)}</Typography>
          ) : null,
      },
      {
        accessorKey: "total_purchase_orders",
        Cell: ({ row }) => formatNumber(row.original.total_purchase_orders),
        header: "Purchase Orders-Created",
        size: 10,
        Footer: () =>
          viewSummary?.total_purchase_orders ? (
            <Typography fontWeight={600}>
              {formatNumber(viewSummary.total_purchase_orders)}
            </Typography>
          ) : null,
      },
      {
        accessorKey: "cancelled_purchase_orders",
        Cell: ({ row }) => formatNumber(row.original.cancelled_purchase_orders),
        header: "Purchase Orders-Cancelled ",
        size: 10,
        Footer: () =>
          viewSummary?.cancelled_purchase_orders ? (
            <Typography fontWeight={600}>
              {formatNumber(viewSummary.cancelled_purchase_orders)}
            </Typography>
          ) : null,
      },
      {
        accessorKey: "total_sales_orders",
        Cell: ({ row }) => formatNumber(row.original.total_sales_orders),
        header: "Invoices-Created",
        size: 10,
        Footer: () =>
          viewSummary?.total_sales_orders ? (
            <Typography fontWeight={600}>
              {formatNumber(viewSummary.total_sales_orders)}
            </Typography>
          ) : null,
      },
      {
        accessorKey: "cancelled_sales_orders",
        Cell: ({ row }) => formatNumber(row.original.cancelled_sales_orders),
        header: "Invoices-Cancelled ",
        size: 10,
        Footer: () =>
          viewSummary?.cancelled_sales_orders ? (
            <Typography fontWeight={600}>{formatNumber(viewSummary.cancelled_sales_orders)}</Typography>
          ) : null,
      },
      // {
      //   accessorKey: "expired_products",
      //   header: "Expired Products",
      //   size: 10,
      //   Footer: () => viewSummary?.expired_products ?? "-",
      //   enableColumnFilter: false,
      // },
      // {
      //   accessorKey: "total_stock_qty",
      //   header: "Total Quantity In Stock ",
      //   size: 10,
      //   Footer: () => viewSummary?.total_stock_qty ?? "-",
      //   enableColumnFilter: false,
      // },

      {
        accessorKey: "View Store",
        header: "View Store",
        size: 10,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <Tooltip title="View Store Details" arrow>
            <IconButton
              color="primary"
              sx={{ cursor: "pointer" }}
              onClick={() => {
                setStoreName(row.original.store_name);
                navigate(`/reports/stock-report/${row.original.store_id}`);
              }}
            >
              <RemoveRedEyeRounded />
            </IconButton>
          </Tooltip>
        ),
      },
    ];
  }, [loading, viewSummary]);

  const Innercolumns = useMemo(() => {
    return [
      {
        accessorKey: "product_name",
        header: "Product Name",
        size: 10,
        Footer: () => "Total",
      },

      {
        accessorKey: "in_stock",
        header: "In Stock",
        size: 10,
        Cell: ({ row }) => formatNumber(row.original.in_stock),
        Footer: () => (summary?.in_stock != null ? formatNumber(summary.in_stock) : "-"),
      },
      {
        accessorKey: "added_quantity",
        header: "Added Quantity",
        size: 10,
        Cell: ({ row }) => formatNumber(row.original.added_quantity),
        Footer: () => (summary?.added_quantity != null ? formatNumber(summary.added_quantity) : "-"),
      },

      {
        accessorKey: "transferred_in",
        header: "Transferred Quantity",
        size: 10,
        Cell: ({ row }) => formatNumber(row.original.transferred_in),
        Footer: () => (summary?.transferred_in != null ? formatNumber(summary.transferred_in) : "-"),
      },

      {
        accessorKey: "deducted_quantity",
        header: "Deducted Quantity",
        size: 10,
        Cell: ({ row }) => formatNumber(row.original.deducted_quantity),
        Footer: () => (summary?.deducted_quantity != null ? formatNumber(summary.deducted_quantity) : "-"),
      },
      {
        accessorKey: "expires_in",
        header: "Expires In (Days)",
        size: 10,
        Footer: () => summary?.expires_in ?? "-",
      },
      {
        accessorKey: "manufacture_date",
        header: "Manufactured Date",
        size: 10,
        ...dateColumnFilterProps(),
        Cell: ({ row }) =>
          moment(row.original.manufacture_date).format("DD-MM-YYYY"),
        Footer: () => summary?.manufacture_date ?? "-",
      },
      // {
      //   accessorKey: "expired_products",
      //   header: "Expired Products",
      //   size: 10,
      //   Footer: () => summary?.expired_products ?? "-",
      //   enableColumnFilter: false,
      // },
    ];
  }, [loading]);

  return (
    <>
      {/* {loading ? (
        <Spinner />
      ) : ( */}
      <Layout>
        <Snackbar
          open={!!successMessage}
          autoHideDuration={3000} // hide after 3 seconds
          onClose={() => setSuccessMessage("")}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSuccessMessage("")}
            severity="success"
            sx={{
              width: "100%",
              backgroundColor: "#28a745",
              color: "#fff",
              fontWeight: 600,
            }}
          >
            {successMessage}
          </Alert>
        </Snackbar>
        <Grid className="employee-form bold-header">
          <Grid
            container
            spacing={3}
            alignItems="center"
            justifyContent="space-between"
          >
            {/* Left section */}
            <Grid item xs={12} md={6}>
              <Typography className="page_title" textAlign="left">
                Stock Inventory Report
              </Typography>
              {storeId && storeName !== "" && (
                <Typography textAlign="left" variant="subtitle1">
                  Source : {storeName}
                </Typography>
              )}
              <Breadcrumbs aria-label="breadcrumb" sx={{ mt: 1 }}>
                <Link underline="hover" color="inherit" href="/dashboard">
                  Dashboard
                </Link>
                <Typography color="text.primary">
                  Stock Inventory Report
                </Typography>
              </Breadcrumbs>

              {/* {storeId && storeName !== "" && (
                <Typography textAlign="left" variant="subtitle1">
                  Store: {storeName}
                </Typography>
              )} */}
            </Grid>
            <Grid item xs={12} lg={6} md={6} textAlign="right">
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate(-1)}
                startIcon={<ArrowBackIosIcon />}
                sx={{ ml: 2 }}
              >
                Back
              </Button>
            </Grid>
            {storeId && storeName !== "" ? (
              <Grid container spacing={2} ml={1} mt={2} alignItems="center">
                {/* From Date */}
                <Grid item xs={12} md={4} lg={6}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <MobileDatePicker
                      label="From Date"
                      value={fromDate}
                      maxDate={toDate}
                      inputFormat="DD-MM-YYYY"
                      onChange={(newValue) => setFromDate(newValue)}
                      renderInput={(params) => (
                        <Tooltip
                          title="Select date to filter products by manufacture date"
                          arrow
                        >
                          <TextField {...params} fullWidth />
                        </Tooltip>
                      )}
                    />
                  </LocalizationProvider>
                </Grid>

                {/* To Date */}
                <Grid item xs={12} md={4} lg={6}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <MobileDatePicker
                      label="To Date"
                      minDate={fromDate}
                      value={toDate}
                      inputFormat="DD-MM-YYYY"
                      onChange={(newValue) => setToDate(newValue)}
                      renderInput={(params) => (
                        <Tooltip
                          title="Select date to filter products by manufacture date"
                          arrow
                        >
                          <TextField {...params} fullWidth />
                        </Tooltip>
                      )}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid
                  item
                  xs={12}
                  container
                  justifyContent="center"
                  spacing={2}
                  // sx={{ mt: 1 }}
                >
                  <Grid item>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSearch}
                      sx={{ textTransform: "none", borderRadius: "8px" }}
                    >
                      Search
                    </Button>
                  </Grid>

                  <Grid item>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => {
                        setFromDate(null);
                        setToDate(null);
                        setColumnFilters([]);
                        handleClear();
                      }}
                      sx={{ textTransform: "none", borderRadius: "8px" }}
                    >
                      Clear
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            ) : (
              <></>
            )}
          </Grid>

          <Grid mt={2}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              size="small"
              color="primary"
              sx={reportViewToggleGroupSx}
              onChange={(e, v) => v && setViewMode(v)}
            >
              <ToggleButton value="store" sx={reportViewToggleButtonSx}>
                Store-wise
              </ToggleButton>
              <ToggleButton value="product" sx={reportViewToggleButtonSx}>
                Product-wise
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>

          {!storeId && (
            <Grid mt={2}>
              <ListFilterBar
                value={bar}
                onChange={setBar}
                onClear={() => setBar({ ...emptyListFilters })}
                showCustomer={false}
                showSalesPerson={false}
                showDates={false}
                showSearch={false}
              />
            </Grid>
          )}

          <Grid mt={2}>
            {viewMode === "product" ? (
              <ProductWiseInventory alert={dashAlert} onClearAlert={() => setDashAlert(null)} storeId={bar.storeId} />
            ) : (
            <MaterialReactTable
              enableGlobalFilter={false}
              enableGlobalFiltering={false}
              columns={storeId ? Innercolumns : columns}
              data={!storeId && Number(bar.storeId) > 0 ? tblList.filter((r) => Number(r.store_id) === Number(bar.storeId)) : tblList}
              enableTableFooter
              muiTableFooterProps={{
                sx: {
                  position: "sticky",
                  bottom: 0,
                  zIndex: 100,
                  backgroundColor: "#fff", // match table background
                  boxShadow: "0px -2px 8px rgba(0,0,0,0.1)",
                },
              }}
              muiToolbarAlertBannerProps={{
                sx: {
                  "& .MuiButtonBase-root": {
                    color: "red",
                    fontWeight: "500",
                    width: "100px",
                    "&:hover": {
                      background:
                        "linear-gradient(90deg, rgba(255, 153, 153, 0.8) 30%, rgba(255, 187, 187, 0.9) 100%)",
                    },
                  },
                },
              }}
              getRowId={(row) => row.id}
              initialState={{ showColumnFilters: false }}
              enableRowNumbers
              rowNumberMode="original"
              enableFullScreenToggle={false}
              enableStickyHeader
              manualFiltering
              manualPagination
              manualSorting
              onColumnFiltersChange={setColumnFilters}
              onGlobalFilterChange={setGlobalFilter}
              onPaginationChange={setPagination}
              onSortingChange={setSorting}
              rowCount={rowCount}
              muiLinearProgressProps={{
                color: "inherit",
                sx: { height: "4px" },
              }}
              state={{
                rowSelection: selectedRowIds,
                columnFilters,
                globalFilter,
                isLoading: loading,
                pagination,
                // showAlertBanner: isError,
                // showProgressBars: isRefetching,
                sorting,
              }}
              muiTableContainerProps={{
                sx: {
                  maxHeight: "calc(100vh - 240px)",
                  minHeight: 450,
                  overflowY: "auto",
                },
              }}
              muiTableBodyCellProps={{
                sx: {
                  padding: "3px 8px",
                  border: "1px solid #ccc",
                },
              }}
              muiTableHeadCellProps={{
                sx: {
                  padding: "8px",
                  border: "1px solid #ccc",
                  color: "#fff !important",
                  fontWeight: "bold",
                  backgroundColor: "#1976d2",
                  "& svg": {
                    color: "#fff !important",
                  },
                  "& .MuiTableSortLabel-root": {
                    color: "#fff !important",
                    "&.Mui-active": {
                      color: "#fff !important",
                      "& .MuiTableSortLabel-icon": {
                        color: "#fff !important",
                      },
                    },
                  },
                  "& .MuiTableSortLabel-icon": {
                    color: "#fff !important",
                  },
                  "& .MuiInputBase-input": {
                    color: "#fff !important",
                    textFillColor: "#fff !important",
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "#fff !important",
                    opacity: 0.8,
                  },
                  "& .MuiInput-underline:before": {
                    borderBottomColor: "#fff !important",
                  },
                  "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                    borderBottomColor: "#fff !important",
                  },
                  "& .MuiInput-underline:after": {
                    borderBottomColor: "#fff !important",
                  },
                },
              }}
              muiTableBodyRowProps={({ row }) => ({
                sx: {
                  borderBottom: "1px solid #ccc",
                  backgroundColor: row.index % 2 === 0 ? "#ffffff" : "#e3f2fd",
                },
              })}
              muiTableHeadCellFilterTextFieldProps={{
                sx: {
                  "& input": {
                    color: "#fff !important", // Change text color
                  },
                  "& .MuiInput-underline:before": {
                    borderBottomColor: "#fff !important", // Change underline color
                  },
                  "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                    borderBottomColor: "#fff !important", // Hover underline color
                  },
                  "& .MuiInput-underline:after": {
                    borderBottomColor: "#fff !important", // Focused underline color
                  },
                },
              }}
              renderTopToolbarCustomActions={({ table }) => (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      gap: "1rem",
                      p: "0.5rem",
                      justifyContent: "space-between",
                      flexGrow: 1,

                      flexWrap: "wrap",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {/* RIGHT SIDE BUTTON */}
                      <Tooltip title="Export data to excel sheet" arrow>
                        <Button
                          onClick={handleExportData}
                          style={{ background: "#1a73e829" }}
                          startIcon={<FileDownload />}
                          // startIcon={<IconTableExport />}
                        >
                          Export To Excel
                        </Button>
                      </Tooltip>
                    </Box>
                  </Box>
                </>
              )}
            />
            )}
          </Grid>
        </Grid>
      </Layout>
    </>
  );
};

export default StoreInventoryReports;
